import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {HttpHeaders, HttpClient, HttpResponse} from '@angular/common/http';
import {ApiService} from './api.service';
import {Pagination} from "@core/shared/models/pagination";
import {User} from "@core/shared/models/user";
import {AuthenticationService} from "@core/shared/services/authentication.service";
import {Role} from "@core/shared/models/role";
import {JwtToken} from "@core/shared/models/jwt";
import {environment} from "../../../../environments/environment";
import {Access, AccessType} from "@core/shared/models/access";
import {AdditionalHeader} from "@core/shared/models/http-client";

@Injectable({
    providedIn: 'root'
})
export class UserService {

    public currentUser: BehaviorSubject<User> = new BehaviorSubject(null);

    constructor(
        private _apiService: ApiService,
        private _httpClient: HttpClient,
        private _authenticationService: AuthenticationService
    ) {
        this._authenticationService.jwtToken.subscribe((data: JwtToken): void => {

            this.refreshUser(data.id);
        });
    }

    public refreshUser(id: number|null, callback: () => void = (): void => {}): void {

        if (!id) {

            this.currentUser.next(null);

            callback();

        } else {

            this.getItemAPI(id).subscribe((user: User): void => {

                this.currentUser.next(user);

                callback();

            }, (): void => {

                if(!environment.production){

                    return;
                }

                this._authenticationService.logout();
            });
        }
    }

    public getPaginationItemsAPI(params?: string[]): Observable<Pagination<User>> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        let url: string = `${this._apiService.getApiUrl(false, true)}/users`;

        params = params || [];

        if (params.length > 0) {

            url = `${url}?${params.join('&')}`;
        }

        return this._httpClient.get<Pagination<User>>(url, {
            headers: headers
        });

    }

    public getItemAPI(id: number, additionalHeaders: AdditionalHeader[] = []): Observable<User> {

        let headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        additionalHeaders?.forEach(header => headers = headers.append(header.name, header.value.toString()));

        return this._httpClient.get<User>(`${this._apiService.getApiUrl(false, true)}/users/${id}`, {
            headers: headers
        });
    }

    public getPaginationItemSocietyUsers(id: number, params?: string[]): Observable<Pagination<User>> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        let url: string = `${this._apiService.getApiUrl(false, true)}/users/${id}/users`;

        params = params || [];

        if (params.length > 0) {

            url = `${url}?${params.join('&')}`;
        }

        return this._httpClient.get<Pagination<User>>(url, {
            headers: headers
        });
    }

    public createItemAPI(data: object): Observable<User> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        return this._httpClient.post<User>(`${this._apiService.getApiUrl(false, true)}/users`, data, {
            headers: headers
        });
    }

    public updateItemAPI(id: number, data: object): Observable<User> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        return this._httpClient.patch<User>(`${this._apiService.getApiUrl(false, true)}/users/${id}`, data, {
            headers: headers
        });
    }

    public deleteItemAPI(id: number): Observable<void> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        return this._httpClient.delete<void>(`${this._apiService.getApiUrl(false, true)}/users/${id}`, {
            headers: headers
        });
    }

    public validateAccountAPI(token: string, data: object): Observable<void> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        return this._httpClient.post<void>(`${this._apiService.getApiUrl(false, false)}/public/users/validate-account/${token}`, data, {
            headers: headers
        });
    }

    public resetAccountPasswordRequestAPI(data: object): Observable<void> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        return this._httpClient.post<void>(`${this._apiService.getApiUrl(false, false)}/public/users/reset-password/request`, data, {
            headers: headers
        });
    }

    public resetAccountPasswordAPI(token: string, data: object): Observable<void> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        return this._httpClient.post<void>(`${this._apiService.getApiUrl(false, false)}/public/users/reset-password/validate/${token}`, data, {
            headers: headers
        });
    }

    public exportItemsAPI(): Observable<Blob> {

        return this._httpClient.get(`${this._apiService.getApiUrl(false, true)}/users/export`, {
            responseType: 'blob'
        });
    }

    public getLoginLogoAPI(params?: string[]): Observable<HttpResponse<Blob>> {

        let url: string = `${this._apiService.getApiUrl(false, true)}/public/users/login/logo`;

        params = params || [];

        if (params.length > 0) {

            url = `${url}?${params.join('&')}`;
        }

        return this._httpClient.get(url, {
            observe: 'response',
            responseType: 'blob'
        });
    }

    public hasRole(user: User, role: Role): boolean {

        if(!user){

            return false;
        }

        return user.roles.includes(role);
    }

    public hasOneOfThisRoles(user: User, roles: Role[]): boolean {

        if(!user){

            return false;
        }

        return roles.some((role: Role): boolean => {

            return this.hasRole(user, role);
        });
    }

    public hasAllOfThisRoles(user: User, roles: Role[]): boolean {

        if(!user){

            return false;
        }

        return roles.every((role: Role): boolean => {

            return this.hasRole(user, role);
        });
    }

    public hasAccess(user: User, accessTag: AccessType): boolean {

        if(!user){

            return false;
        }

        return user.accesses.some((item: Access): boolean => {

            return item.tag === accessTag;
        });
    }

    public isCurrentUserWithAccess(accessTag: AccessType): boolean {

        const user: User = this.currentUser.getValue();

        return user ? this.hasAccess(user, accessTag) : false;
    }

    public hasOneOfTheseAccesses(user: User, accessTags: AccessType[]): boolean {

        if(!user){

            return false;
        }

        return accessTags.some((item: AccessType): boolean => {

            return this.hasAccess(user, item);
        });
    }

    public hasAllOfTheseAccesses(user: User, accessTags: AccessType[]): boolean {

        if(!user){

            return false;
        }

        return accessTags.every((item: AccessType): boolean => {

            return this.hasAccess(user, item);
        });
    }

    public isUserWithSociety(user: User): boolean {

        return this.hasOneOfThisRoles(user, ['ROLE_OFFER_CREATOR', 'ROLE_OFFER_DISTRIBUTOR', 'ROLE_PROVIDER', 'ROLE_INSTITUTIONAL', 'ROLE_FEDERATION']);
    }

    public isUserAdmin(user: User): boolean {

        return this.hasOneOfThisRoles(user, ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN']);
    }

    public isUserOfferCreator(user: User, checkOfferDistributorState: boolean = false, isUserOfferDistributor: boolean = false): boolean {

        if(!checkOfferDistributorState){

            return this.hasRole(user, 'ROLE_OFFER_CREATOR');
        }

        if(isUserOfferDistributor){

            return this.hasAllOfThisRoles(user, ['ROLE_OFFER_CREATOR', 'ROLE_OFFER_DISTRIBUTOR']);
        }
        else {

            return this.hasRole(user, 'ROLE_OFFER_CREATOR') && !this.hasRole(user, 'ROLE_OFFER_DISTRIBUTOR');
        }
    }

    public isCurrentUserOfferCreator(checkOfferDistributorState: boolean = false, isUserOfferDistributor: boolean = false): boolean {

        const user: User = this.currentUser.getValue();

        return user ? this.isUserOfferCreator(user, checkOfferDistributorState, isUserOfferDistributor) : false;
    }

    get isCurrentUserWithSociety(): boolean {

        const user: User = this.currentUser.getValue();

        return user ? this.isUserWithSociety(user) : false;
    }

    get isCurrentUserAdmin(): boolean {

        const user: User = this.currentUser.getValue();

        return user ? this.isUserAdmin(user) : false;
    }

    get isCurrentUserCollaborator(): boolean {

        const user: User = this.currentUser.getValue();

        return user ? !!!user.societyAdmin : false;
    }
}
